/**
 * URL slug helpers for job detail pages.
 *
 * Goal: replace `/jobs/<uuid>` with `/jobs/<keyword-slug>-<short-id>`
 * to give every job a keyword-rich, crawler-friendly URL while preserving
 * lookup by ID. The short-id (last 12 chars of the UUID, hyphens stripped)
 * is unique enough across our dataset without exposing the full UUID and
 * matches the `short_id` column on the jobs table.
 */

export interface SluggableJob {
  id: string;
  title: string | null | undefined;
  company?: string | null | undefined;
  is_remote?: boolean | null | undefined;
}

export function shortJobId(id: string): string {
  return id.replace(/-/g, '').slice(-12).toLowerCase();
}

function slugify(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

export function buildJobSlug(job: SluggableJob): string {
  const parts: string[] = [];
  if (job.title) parts.push(slugify(job.title));
  if (job.company) parts.push(slugify(job.company));
  if (job.is_remote) parts.push('remote');
  const base = parts.filter(Boolean).join('-').slice(0, 60).replace(/-+$/, '');
  const suffix = shortJobId(job.id);
  return base ? `${base}-${suffix}` : suffix;
}

export function buildJobPath(job: SluggableJob): string {
  return `/jobs/${buildJobSlug(job)}`;
}

/**
 * Fallback for call sites that only know the job UUID (no title/company).
 * Returns `/jobs/<12-char-shortid>`; the JobDetailPage will fetch the job
 * and `replace`-navigate to the canonical slug URL on load.
 */
export function buildJobPathFromId(id: string): string {
  return `/jobs/${shortJobId(id)}`;
}

export function parseJobSlug(segment: string): { shortId: string; isLegacyUuid: boolean } {
  const trimmed = segment.trim();
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)) {
    return { shortId: shortJobId(trimmed), isLegacyUuid: true };
  }
  const m = trimmed.match(/-([0-9a-f]{12})$/i);
  if (m) return { shortId: m[1].toLowerCase(), isLegacyUuid: false };
  if (/^[0-9a-f]{12}$/i.test(trimmed)) {
    return { shortId: trimmed.toLowerCase(), isLegacyUuid: false };
  }
  return { shortId: trimmed.toLowerCase(), isLegacyUuid: false };
}
